// SMSTemplateForm.js - Skapa/redigera SMS-mall
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../apiClient';
import { UserContext } from '../../UserContext';
import { toast } from 'react-toastify';
import './SMSTemplateForm.css';

function SMSTemplateForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const isEditing = !!id;

  // Form state
  const [templateName, setTemplateName] = useState('');
  const [body, setBody] = useState('');
  const [clinicId, setClinicId] = useState('');
  const [clinics, setClinics] = useState([]);
  const [variables, setVariables] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Ref för textarea
  const bodyRef = useRef(null);

  // Hämta kliniker och variabler
  useEffect(() => {
    apiClient.get('/api/clinics')
      .then(res => setClinics(res.data))
      .catch(err => console.error('Fel vid hämtning av kliniker:', err));

    apiClient.get('/api/sms/variables')
      .then(res => setVariables(res.data))
      .catch(err => console.error('Fel vid hämtning av variabler:', err));
  }, []);

  // Förifyll komplett, redigerbart exempel vid SKAPA ny (inte vid redigering).
  useEffect(() => {
    if (id) return; // redigering hämtar befintligt innehåll nedan
    apiClient.get('/api/sms/defaults')
      .then(res => {
        const d = res.data?.sms || {};
        setBody(prev => prev || d.body || '');
      })
      .catch(err => console.error('Fel vid hämtning av standardinnehåll:', err));
  }, [id]);

  // Hämta befintlig mall vid redigering
  useEffect(() => {
    if (id) {
      apiClient.get(`/api/sms/templates/${id}`)
        .then(res => {
          const data = res.data;
          setTemplateName(data.template_name || '');
          setBody(data.body || '');
          setClinicId(data.clinic_id || '');
        })
        .catch(err => {
          console.error('Fel vid hämtning av mall:', err);
          setError('Kunde inte hämta mallen');
        });
    }
  }, [id]);

  // Infoga variabel vid markörposition
  const insertVariable = (variable) => {
    if (bodyRef.current) {
      const textarea = bodyRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = body.slice(0, start) + variable + body.slice(end);
      setBody(newValue);
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + variable.length;
        textarea.focus();
      }, 0);
    }
  };

  // Spara mall
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!templateName.trim()) {
      toast.error('Mallnamn krävs');
      return;
    }
    if (!body.trim()) {
      toast.error('Mallinnehåll krävs');
      return;
    }

    setSaving(true);
    setError('');

    const payload = {
      template_name: templateName,
      body: body,
      clinic_id: clinicId || null
    };

    try {
      if (isEditing) {
        await apiClient.put(`/api/sms/templates/${id}`, payload);
        toast.success('SMS-mall uppdaterad');
      } else {
        await apiClient.post('/api/sms/templates', payload);
        toast.success('SMS-mall skapad');
      }
      navigate('/sms-mallar');
    } catch (err) {
      console.error('Fel vid sparande:', err);
      toast.error(err.response?.data?.msg || 'Fel vid sparande');
      setError(err.response?.data?.msg || 'Fel vid sparande');
    } finally {
      setSaving(false);
    }
  };

  // Teckenräknare
  const charCount = body.length;
  const smsCount = Math.ceil(charCount / 160) || 1;

  return (
    <div className="sms-form-container">
      <div className="sms-form-main">
        <div className="form-header">
          <button className="back-btn" onClick={() => navigate('/sms-mallar')}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <h2>{isEditing ? 'Redigera SMS-mall' : 'Skapa SMS-mall'}</h2>
        </div>

        {error && <p className="error-message">{error}</p>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="templateName">Mallnamn *</label>
            <input
              type="text"
              id="templateName"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="T.ex. Påminnelse om bokning"
              required
            />
          </div>

          {user?.is_admin && (
            <div className="form-group">
              <label htmlFor="clinicId">Klinik</label>
              <select
                id="clinicId"
                value={clinicId}
                onChange={(e) => setClinicId(e.target.value)}
              >
                <option value="">Global mall (alla kliniker)</option>
                {clinics.map(c => (
                  <option key={c.clinic_id} value={c.clinic_id}>
                    {c.clinic_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="body">Meddelande *</label>
            <textarea
              ref={bodyRef}
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={`Hej {firstname}!

Glöm inte din tid hos oss.

Mvh {kliniknamn}`}
              rows={8}
              required
            />
            <div className="char-info">
              <span className={charCount > 160 ? 'warning' : ''}>
                {charCount} tecken
              </span>
              <span className="sms-indicator">
                {smsCount} SMS {smsCount > 1 && <span className="note">(långt meddelande)</span>}
              </span>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/sms-mallar')}
              className="cancel-button"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={saving}
              className="save-button"
            >
              {saving ? 'Sparar...' : (isEditing ? 'Uppdatera' : 'Skapa mall')}
            </button>
          </div>
        </form>
      </div>

      <div className="variable-sidebar">
        <h3>Tillgängliga variabler</h3>
        <p className="variable-help">Klicka för att infoga i meddelandet</p>

        <div className="variable-instruktion">
          <p>
            Skriv variabler som <code>{'{firstname}'}</code> direkt i texten — de
            byts automatiskt ut mot patientens uppgifter när SMS:et skickas. OBS:
            SMS är inte kopplat till en bokning, så <code>{'{datum}'}</code> och
            <code>{' {tid}'}</code> finns inte här. Nya mallar fylls från start
            med ett exempel du kan bygga vidare på.
          </p>
        </div>

        <div className="variable-list">
          {variables.map(v => (
            <button
              key={v.variable}
              type="button"
              className="variable-button"
              onClick={() => insertVariable(v.variable)}
            >
              <span className="variable-name">{v.variable}</span>
              <span className="variable-desc">{v.description}</span>
            </button>
          ))}
        </div>

        <div className="sms-tips">
          <h4>Tips för SMS</h4>
          <ul>
            <li>Håll meddelandet kort (under 160 tecken = 1 SMS)</li>
            <li>Längre meddelanden delas upp och kostar mer</li>
            <li>Undvik specialtecken som kan ta extra plats</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default SMSTemplateForm;
